import { Eye, FileBadge2, RefreshCw, Trash2, Upload } from 'lucide-react';
import { useCallback, useState } from 'react';
import { toast } from 'sonner';

import { PhotoIdUploadModal } from '@/components/matter-notes-v2/matter-client-v2/components/client-photo-id/PhotoIdUploadModal';
import { Button } from '@/components/ui/catalyst/button';
import { Text } from '@/components/ui/catalyst/text';
import { useGetBlobDetailQuery } from '@/services/api/blobService';
import { treatmentRecordRequestService } from '@/services/api/treatmentRecordRequestService';

import { useRequestPage } from '../contexts/RequestPageContext';

export const ReviewClientIdentificationSection = () => {
  const { matterId, primaryClient, refetchPackage } = useRequestPage();
  const [isUploadOpen, setIsUploadOpen] = useState(false);

  const { data: existingAuth, refetch: refetchAuthorization } =
    treatmentRecordRequestService.useMattersHipaaAuthorizationForMatterRetrieveQuery(
      { matterId },
    );

  const [createAuthorization, { isLoading: isCreating }] =
    treatmentRecordRequestService.useMattersHipaaAuthorizationCreateMutation();
  const [updateAuthorization, { isLoading: isUpdating }] =
    treatmentRecordRequestService.useMattersHipaaAuthorizationUpdateMutation();

  const existingBlobId = existingAuth?.client_physical_id || null;
  const { data: photoIdBlob } = useGetBlobDetailQuery(
    { matterId, blobId: existingBlobId || '' },
    { skip: !existingBlobId },
  );

  const isSaving = isCreating || isUpdating;
  const hasPhotoId = Boolean(existingBlobId && photoIdBlob);
  const isImage = photoIdBlob?.file_mime_type?.startsWith('image/');
  const previewUrl = isImage ? photoIdBlob?.download_url : null;

  const handleSelect = useCallback(
    async (blobId: string) => {
      if (!primaryClient?.id) {
        toast.error('Primary client not found');
        return;
      }

      try {
        if (existingAuth?.id) {
          await updateAuthorization({
            matterId,
            authorizationId: existingAuth.id,
            updateTreatmentRecordRequestHipaaAuthorizationSchema: {
              client_physical_id: blobId,
              authorization_template_id:
                existingAuth.authorization_template_id,
            },
          }).unwrap();
        } else {
          await createAuthorization({
            matterId,
            createTreatmentRecordRequestHipaaAuthorizationSchema: {
              client_physical_id: blobId,
            },
          }).unwrap();
        }

        toast.success('Client identification attached');
        setIsUploadOpen(false);
        await refetchAuthorization();
        refetchPackage();
      } catch {
        toast.error('Failed to attach client identification');
      }
    },
    [
      createAuthorization,
      existingAuth?.authorization_template_id,
      existingAuth?.id,
      matterId,
      primaryClient?.id,
      refetchAuthorization,
      refetchPackage,
      updateAuthorization,
    ],
  );

  const handleClear = useCallback(async () => {
    if (!existingAuth?.id) return;

    try {
      await updateAuthorization({
        matterId,
        authorizationId: existingAuth.id,
        updateTreatmentRecordRequestHipaaAuthorizationSchema: {
          client_physical_id: null,
          authorization_template_id: existingAuth.authorization_template_id,
        },
      }).unwrap();

      toast.success('Client identification removed');
      await refetchAuthorization();
      refetchPackage();
    } catch {
      toast.error('Failed to remove client identification');
    }
  }, [
    existingAuth?.authorization_template_id,
    existingAuth?.id,
    matterId,
    refetchAuthorization,
    refetchPackage,
    updateAuthorization,
  ]);

  return (
    <>
      <div className="flex items-start justify-between gap-4 px-1 pt-1">
        <div className="flex min-w-0 items-center gap-2 pt-2">
          <Text className="font-sans text-[15px] font-semibold leading-5 text-zinc-950">
            Client Identification
          </Text>
          <span className="inline-flex h-[20px] items-center rounded-full bg-zinc-100 px-2.5 font-sans text-[11px] font-medium leading-4 text-zinc-500">
            Optional
          </span>
        </div>

        <div className="w-[220px] shrink-0">
          {hasPhotoId ? (
            <div className="overflow-hidden rounded-[16px] border border-zinc-200 bg-white">
              <button
                type="button"
                onClick={() => setIsUploadOpen(true)}
                disabled={isSaving}
                className="group block h-[104px] w-full bg-zinc-50 transition hover:bg-zinc-100 disabled:cursor-not-allowed"
              >
                {previewUrl ? (
                  <div className="relative h-full w-full">
                    <img
                      src={previewUrl}
                      alt="Client identification preview"
                      className="h-full w-full object-cover"
                    />
                    <div className="absolute inset-0 flex items-center justify-center bg-black/0 transition group-hover:bg-black/10">
                      <Eye className="h-5 w-5 text-white opacity-0 transition group-hover:opacity-100" />
                    </div>
                  </div>
                ) : (
                  <div className="flex h-full items-center justify-center gap-3 px-4">
                    <div className="rounded-[12px] bg-white p-2.5 shadow-sm ring-1 ring-zinc-200">
                      <FileBadge2 className="h-4 w-4 text-zinc-500" />
                    </div>
                    <Text className="line-clamp-2 text-left font-sans text-[13px] font-medium leading-5 text-zinc-900">
                      {photoIdBlob?.name ||
                        photoIdBlob?.original_filename ||
                        'Client ID'}
                    </Text>
                  </div>
                )}
              </button>
              <div className="flex items-center justify-between border-t border-zinc-200 px-3 py-2">
                <Button
                  plain
                  onClick={() => setIsUploadOpen(true)}
                  className="h-7 px-0 font-sans text-[12px] font-medium text-zinc-700 hover:text-zinc-950"
                  disabled={isSaving}
                >
                  <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
                  Replace
                </Button>
                <Button
                  plain
                  onClick={handleClear}
                  className="h-7 px-0 font-sans text-[12px] font-medium text-zinc-500 hover:text-red-600"
                  disabled={isSaving}
                >
                  <Trash2 className="mr-1.5 h-3.5 w-3.5" />
                  Remove
                </Button>
              </div>
            </div>
          ) : (
            <Button
              outline
              onClick={() => setIsUploadOpen(true)}
              disabled={!primaryClient?.id || isSaving}
              className="!items-center h-9 w-full rounded-[10px] border-zinc-300 bg-white px-4 font-sans text-[14px] font-medium text-zinc-800 shadow-sm"
            >
              <Upload className="mr-1.5 h-[14px] w-[14px]" />
              Upload Photo ID
            </Button>
          )}
        </div>
      </div>

      <PhotoIdUploadModal
        isOpen={isUploadOpen}
        onClose={() => setIsUploadOpen(false)}
        onSelect={handleSelect}
        onClear={hasPhotoId ? handleClear : undefined}
        matterId={matterId}
        isUploading={isSaving}
        existingBlobId={existingBlobId}
      />
    </>
  );
};
